import React, { useState, useCallback } from 'react';
import { isApiError } from '../../../lib/apiClient';
import { Box, Stack, Button, Typography, Chip } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { grey } from '@mui/material/colors';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CustomSwitch from './CustomSwitch';
import './Footer.css';
import ConfirmDialog from '../../common/ConfirmDialog';
import AlertDialog from '../../common/AlertDialog';
import { extractApiErrorMessage } from '../../../utils/apiErrorMessage';
import { useNavigate } from 'react-router-dom';

import type { DynamicFieldItem } from '../../../types/weeklyReport';
import {
  postNewWeeklyReportVersion,
  postWeeklyReport,
  postWeeklyReportOverwrite,
  postWeeklyReportDelete,
} from '../../../api/weeklyReport';
import { uploadImageAttachments } from '../../common/ImageAddDialog/ImageUploadManager';
import { useUserName } from '../../../context/UserNameContext';
import { fetchLatestWeeklyReportPublicSettings } from '../../../api/weeklyReportPublicSettings';
import {
  getSelectedWeeklyReportOptions,
  shouldWarnBeforeWeeklyReportPublication,
} from '../../../lib/weeklyReportPublishScopeWarning';
import {
  buildWeeklyReportPublicScopeDisplayMap,
  DEFAULT_PUBLIC_SCOPE_DISPLAY,
} from '../../../lib/weeklyReportPublicScopeDisplay';
import { useDivisionOptions, useTeamOptions } from '../../../state/master';

// InputEdit 側で使っている本物の型に揃える
import type { SearchSelectionPayload } from '../SearchArea/OverwriteInputSearchArea';
import type { ImageAttachment } from '../../../types/imageAttachment';
import {
  deleteEnlightSyncOverride,
  deleteRemandEnlightSyncPreference,
  persistOverwriteEnlightSyncOutcome,
} from '../enlightSync';
import { missingRequiredDynamicTypes } from '../requiredDynamicFields';
import {
  CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  FIXED_ATTR_NAME_PROJECT,
  FIXED_ATTR_NAME_POWERTRAIN,
  FIXED_ATTR_NAME_SHIMUKE,
  FIXED_ATTR_NAME_LOT,
} from '../../../constants';

const WEEKLY_REPORT_UPDATED_EVENT = 'weekly-report-updated';
const WEEKLY_REPORT_UPDATED_AT_KEY = 'weekly-report-updated-at';

type PublicationScopeDetail = {
  optionId: string;
  classificationLabel: string;
  scopeText: string;
};

const notifyWeeklyReportUpdated = () => {
  const nowIso = new Date().toISOString();

  try {
    localStorage.setItem(WEEKLY_REPORT_UPDATED_AT_KEY, nowIso);
  } catch {
  }

  try {
    window.dispatchEvent(new Event(WEEKLY_REPORT_UPDATED_EVENT));
  } catch {
  }
};

/* =========================================================
   表示可否ユーティリティ
 * ========================================================= */
export type ActiveTab = 'new' | 'overwrite';

type ShowArgs = {
  division?: string;
  activeTab: ActiveTab;
  wrId?: number | null;
  isPosting?: boolean;
};

export const shouldShowRegisterButton = ({
  division,
  activeTab,
  wrId,
  isPosting,
}: ShowArgs): boolean => {
  const isOverwrite = activeTab === 'overwrite';
  if (!division) return false;
  if (isPosting) return false;
  if (isOverwrite && !wrId) return false;
  return true;
};

export const shouldDisableRegisterButton = ({
  activeTab,
  wrId,
  isPosting,
}: Omit<ShowArgs, 'division'>): boolean => {
  const isOverwrite = activeTab === 'overwrite';
  return Boolean(isPosting || (isOverwrite && !wrId));
};

/* =========================================================
   Props
 * ========================================================= */
interface ValidationErrors {
  reporter?: string;
  startDate?: string;
  endDate?: string;
  title?: string;
  body?: string;
}

interface Props {
  division: string;
  reporter: string;
  startDate: string;
  endDate: string;

  // new
  draftTitle: string;
  draftBody: string;

  // overwrite
  title: string;
  setTitle: (v: string) => void;
  body: string;
  setBody: (v: string) => void;

  enlightSyncEnabled: boolean;
  setEnlightSyncEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  resetDraftTitle: () => void;
  resetDraftBody: () => void;
  onSuccess: () => void;
  activeTab: 'new' | 'overwrite';
  searchResults: any[];
  hasOverwriteSelection?: boolean;
  isFormValid: boolean;
  validateForm: () => boolean;
  validationErrors: ValidationErrors;
  overwriteTargetId?: number | null;
  wrId?: number | null;

  approverAM?: string;
  approver3?: string;

  createBy: string;
  updateBy: string;
  modalPinned: boolean;

  /** SearchArea から現在の動的フィールドを取得 */
  getDynamicFields?: () => DynamicFieldItem[];
  /** SearchArea から実際に登録する動的属性値を取得 */
  getDynamicAttributes?: () => Array<{
    attr_id: number;
    attr_name: string;
    attr_value: string;
  }>;

  /** New 用: チーム（ラベル or ID 文字列） */
  team?: string;

  /** Overwrite 用: 履歴から得たチームID（整数） */
  overwriteTeamId?: number | null;

  // 親から解決済みID（無ければ内部解決）
  resolvedDeptId?: number | null;
  resolvedTeamIdForNew?: number | null;

  /** 本文の残り文字数（InputEdit で算出） */
  remainingChars: number;

  /** Overwrite選択中の temporary_flg（0/1） */
  temporaryFlg?: number | null;

  /** FB事項（Overwrite 時に履歴から復元された値） */
  fbNote?: string;

  /** SearchArea の現在の選択ペイロードを取得 */
  getSearchSelectionPayload?: () => SearchSelectionPayload | null;

  /** ログインユーザーの論理ID（メールアドレスなど） */
  loginId?: string;

  /** 添付画像 */
  images: ImageAttachment[];
  onResetImages?: () => void;

  /**
   * ★追加：New の日付クリアを SearchArea に寄せる
   *  - SearchArea 側で datesClearedRef などを「同期的に」立てて persist まで完了させる想定
   */
  onClearNewDates?: () => void;

  /**
   * 互換用（InputEdit 側がまだ渡していてもコンパイル落ちないように）
   * ※この Footer では “直接は呼びません”
   */
  setStartDate?: React.Dispatch<React.SetStateAction<string>>;
  setEndDate?: React.Dispatch<React.SetStateAction<string>>;

  // payload 側の dateRange も落とす（UIクリアだけだと混入し得る）
  setSearchSelectionPayload?: (p: SearchSelectionPayload | null) => void;
  enlightSyncToggleDisabled?: boolean;
  enlightSyncToggleDisabledReason?: string;
}

/* =========================================================
   定数 / ユーティリティ
 * ========================================================= */

const FIXED_ENUM_KEYS: Record<string, string> = {
  [CLASSIFICATION_MARKET_TARGET_ATTR_NAME]: 'reportType',
  [FIXED_ATTR_NAME_PROJECT]: 'project',
  [FIXED_ATTR_NAME_POWERTRAIN]: 'powerTrain',
  [FIXED_ATTR_NAME_SHIMUKE]: 'Shimuke',
  [FIXED_ATTR_NAME_LOT]: 'lot',
};

// 共通フリー欄の旧/新名称を優先順で扱う
const COMMON_FREE_TYPE_PRIORITY = [
  '自由入力欄',
  '自由入力欄（大）',
  'フリー入力欄',
  'フリー入力欄（大）',
  'フリー',
] as const;

const normalizeTypeName = (v: any): string =>
  String(v ?? '').normalize('NFKC').trim();

const isCommonFreeType = (type?: string): boolean => {
  const key = normalizeTypeName(type);
  if (!key) return false;
  return COMMON_FREE_TYPE_PRIORITY.some((name) => normalizeTypeName(name) === key);
};

const findCommonFreeField = (
  fields: DynamicFieldItem[]
): DynamicFieldItem | null => {
  if (!Array.isArray(fields)) return null;
  for (const name of COMMON_FREE_TYPE_PRIORITY) {
    const key = normalizeTypeName(name);
    const hit = fields.find((f) => normalizeTypeName(f?.type) === key);
    if (hit) return hit;
  }
  return fields.find((f) => isCommonFreeType(f?.type)) ?? null;
};

const scalarizeOne = (v: any): string | null => {
  if (v == null) return null;
  if (Array.isArray(v)) return v.length ? scalarizeOne(v[0]) : null;
  if (typeof v === 'object') {
    const s = String(
      (v as any).label ?? (v as any).value ?? (v as any).name ?? ''
    ).trim();
    return s || null;
  }
  const s = String(v ?? '').trim();
  return s || null;
};

const pickSelectorValue = (x: any): string | null => {
  if (x == null) return null;
  if (typeof x === 'object') {
    const val = String((x.value ?? x.id ?? '')).trim();
    const label = String((x.label ?? x.name ?? '')).trim();
    if (val) return label || val;
    if (label) return label;
    return null;
  }
  const s = String(x).trim();
  return s || null;
};

// 単一({value,label}) も配列も両方対応
const toIdLabelArray = (
  v: any
): Array<{ id: number | string; label: string }> => {
  const out: Array<{ id: number | string; label: string }> = [];
  const pushOne = (x: any) => {
    if (x == null) return;
    if (typeof x === 'object') {
      const label = String(x.label ?? x.name ?? x.value ?? '').trim();
      let id: any = x.id ?? x.value ?? null;
      if (id != null && typeof id === 'string' && /^\d+$/.test(id))
        id = parseInt(id, 10);
      if (label) out.push({ id: id ?? label, label });
    } else {
      const label = String(x).trim();
      if (!label) return;
      const id = /^\d+$/.test(label) ? parseInt(label, 10) : label;
      out.push({ id, label });
    }
  };
  if (Array.isArray(v)) v.forEach(pushOne);
  else pushOne(v);
  return out;
};

const toInt = (v?: string | number | null): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v == null) return null;
  const parsed = parseInt(String(v).trim(), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeDate = (raw?: string | null): string | null => {
  if (!raw) return null;
  const s0 = String(raw).trim();
  if (!s0) return null;
  const s = s0.replace(/[./]/g, '-');
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return s0;
  const yyyy = m[1];
  const mm = String(parseInt(m[2], 10)).padStart(2, '0');
  const dd = String(parseInt(m[3], 10)).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/* =========================================================
   収集ヘルパ
 * ========================================================= */
/** 指定タイプのフィールド（表示順のまま） */
const fieldsOfType = (
  fields: DynamicFieldItem[],
  typeName: string
): DynamicFieldItem[] => (fields ?? []).filter((f) => f?.type === typeName);

/** 1フィールドの MultiSelect 値を配列ラベルで取得 */
const labelsFromMulti = (v: any): string[] => {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const x of v) {
    if (x == null) continue;
    if (typeof x === 'object') {
      const s = String(
        (x as any).label ?? (x as any).name ?? (x as any).value ?? ''
      ).trim();
      if (s) out.push(s);
    } else {
      const s = String(x ?? '').trim();
      if (s) out.push(s);
    }
  }
  return out;
};

/**
 * フルネーム配列から同姓判定済みの表示名を生成する。
 * - 同姓でなければ苗字のみ（例: "佐藤 一郎" → "佐藤"）
 * - 同姓がいれば「苗字＋名前の一文字目」（例: "田中 太郎","田中 花子" → "田中太","田中花"）
 */
export const disambiguateNames = (fullNames: string[]): string[] => {
  const parsed = fullNames.map((name) => {
    const parts = String(name ?? '').normalize('NFKC').trim().split(/[\s\u3000]+/);
    const surname = parts[0] || String(name ?? '').trim();
    const firstName = parts.slice(1).join('') || '';
    return { surname, firstName };
  });

  const surnameCount: Record<string, number> = {};
  for (const { surname } of parsed) {
    surnameCount[surname] = (surnameCount[surname] || 0) + 1;
  }

  return parsed.map(({ surname, firstName }) => {
    if (surnameCount[surname] > 1 && firstName) {
      const firstChar = Array.from(firstName)[0] ?? '';
      return surname + firstChar;
    }
    return surname;
  });
};

/** MultiSelect 値からラベルを取得し、同姓判定済みの表示名に変換する */
export const disambiguatedLabelsFromMulti = (v: any): string[] => {
  const fullNames = labelsFromMulti(v);
  return disambiguateNames(fullNames);
};

/* === 承認系の DynamicFieldItem から先頭のラベルを取得するヘルパー === */
const firstLabelOfType = (
  fields: DynamicFieldItem[],
  typeName: string
): string | null => {
  for (const f of fieldsOfType(fields, typeName)) {
    const v: any = (f as any).value;
    if (Array.isArray(v)) {
      const labels = labelsFromMulti(v);
      if (labels.length > 0) return labels[0];
    } else {
      const s = scalarizeOne(v);
      if (s) return s;
    }
  }
  return null;
};

/**
 * 動的フィールドから複数のチームIDを抽出（値はそのまま利用、数値化のみ）
 */
const extractTeamIdsFromFields = (fields: DynamicFieldItem[]): number[] => {
  const hits = fieldsOfType(fields, 'チーム');
  const out: number[] = [];

  const pushVal = (x: any) => {
    if (x == null) return;
    const raw = typeof x === 'object' ? x.value ?? x.id ?? null : x;
    const n = toInt(raw);
    if (n !== null) out.push(n);
  };

  for (const f of hits) {
    const v = (f as any).value;
    if (Array.isArray(v)) v.forEach(pushVal);
    else pushVal(v);
  }

  const seen = new Set<number>();
  return out.filter((n) => (seen.has(n) ? false : (seen.add(n), true)));
};

/* =========================================================
   ペイロード成形
 * ========================================================= */

// 数字だけのラベル（"2563" など）を弾く判定
const isDigitOnly = (s?: string | null): boolean => !!s && /^\d+$/.test(s);

// ラベルだけの配列を生成（数字だけのラベルは除外）
//        さらに重複を除去して順序維持
const toFilteredLabelList = (
  list: Array<{ id: number | string; label: string }>
): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const e of list) {
    const lab = (e?.label ?? '').trim();
    if (!lab) continue;
    if (isDigitOnly(lab)) continue; // 数字だけは除外
    if (seen.has(lab)) continue;
    seen.add(lab);
    out.push(lab);
  }
  return out;
};

const attachFixedEnumerated = (
  payload: Record<string, any>,
  fields: DynamicFieldItem[]
) => {
  // 週報分類 / 仕向け / ロット
  for (const jp of [CLASSIFICATION_MARKET_TARGET_ATTR_NAME, FIXED_ATTR_NAME_SHIMUKE, FIXED_ATTR_NAME_LOT] as const) {
    const baseKey = FIXED_ENUM_KEYS[jp];
    const hits = fieldsOfType(fields, jp);
    const flat: string[] = [];
    for (const f of hits) {
      if (Array.isArray(f.value)) flat.push(...labelsFromMulti(f.value));
      else {
        const s = scalarizeOne(f.value);
        if (s) flat.push(s);
      }
    }

    const seen = new Set<string>();
    let idx = 1;
    for (const s of flat) {
      if (!s || seen.has(s)) continue;
      seen.add(s);
      (payload as any)[`${baseKey}_${idx}`] = s;
      idx++;
    }
  }

  // Project / パワートレイン
  for (const jp of [FIXED_ATTR_NAME_PROJECT, FIXED_ATTR_NAME_POWERTRAIN] as const) {
    const baseKey = FIXED_ENUM_KEYS[jp];
    const hits = fieldsOfType(fields, jp);

    // まず {id,label} の配列
    const flatIdLabel: Array<{ id: number | string; label: string }> = [];
    for (const f of hits) flatIdLabel.push(...toIdLabelArray(f.value));

    // 数字ラベルを除外したラベル配列へ
    const labels = toFilteredLabelList(flatIdLabel);

    // 列挙キーに「ラベル文字列のみ」を設定
    let idx = 1;
    for (const lab of labels) {
      (payload as any)[`${baseKey}_${idx}`] = lab; // 文字列のみ送信
      idx++;
    }
  }
};

/** 連名者メンバー：UIボックスごとに respondents_1..N（配列） */
const attachRespondentsEnumerated = (
  payload: Record<string, any>,
  fields: DynamicFieldItem[]
) => {
  // 「連名者」を respondents として扱う
  const hitsFallback = fieldsOfType(fields, '連名者');
  const hits = hitsFallback;

  if (hits.length === 0) {
    (payload as any).respondents_1 = null;
    return;
  }

  let idx = 1;
  for (const f of hits) {
    const arr = Array.isArray((f as any).value)
      ? labelsFromMulti((f as any).value)
      : (() => {
          const s = scalarizeOne((f as any).value);
          return s ? [s] : [];
        })();

    (payload as any)[`respondents_${idx}`] = arr.length > 0 ? arr : null;
    idx++;
  }
};


const attachApproversEnumerated = (
  payload: Record<string, any>,
  fields: DynamicFieldItem[]
) => {
  const pushSeries = (typeName: string, baseKey: 'approver' | 'approverAm') => {
    const hits = fieldsOfType(fields, typeName);
    let idx = 1;
    for (const f of hits) {
      const s = scalarizeOne((f as any).value);
      if (!s) continue;
      (payload as any)[`${baseKey}_${idx}`] = s;
      idx++;
    }
  };
  pushSeries('承認-3', 'approver');
  pushSeries('承認AM', 'approverAm');
  delete (payload as any).approverAM_1;
  delete (payload as any).approver;
  delete (payload as any).approverAm;
};

const attachIndexedFreeWordsPairedByType = (
  payload: Record<string, any>,
  fields: DynamicFieldItem[]
) => {
  const FIXED_BASE_NAMES_LOCAL = new Set<string>([
    'チーム',
    '記入者',
    '連名者',
    '承認-3',
    '承認AM',
    CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
    '開始終了日付',
    FIXED_ATTR_NAME_PROJECT,
    FIXED_ATTR_NAME_POWERTRAIN,
    FIXED_ATTR_NAME_SHIMUKE,
    FIXED_ATTR_NAME_LOT,
  ]);

  const candidates = (fields ?? []).filter(
    (f) =>
      f &&
      !FIXED_BASE_NAMES_LOCAL.has(f.type) &&
      !isCommonFreeType(f.type) &&
      typeof f.attr_id === 'number'
  );

  const order: number[] = [];
  const seenAttrId = new Set<number>();
  for (const f of candidates) {
    const aid = f.attr_id as number;
    if (!seenAttrId.has(aid)) {
      seenAttrId.add(aid);
      order.push(aid);
    }
  }

  const labelsFromMultiLocal = (v: any): string[] => {
    if (!Array.isArray(v)) return [];
    const out: string[] = [];
    for (const x of v) {
      if (x == null) continue;
      if (typeof x === 'object') {
        const s = String(
          (x as any).label ?? (x as any).name ?? (x as any).value ?? ''
        ).trim();
        if (s) out.push(s);
      } else {
        const s = String(x ?? '').trim();
        if (s) out.push(s);
      }
    }
    return out;
  };

  const scalarizeOneLocal = (v: any): string | null => {
    if (v == null) return null;
    if (Array.isArray(v)) return v.length ? scalarizeOneLocal(v[0]) : null;
    if (typeof v === 'object') {
      const s = String(
        (v as any).label ?? (v as any).value ?? (v as any).name ?? ''
      ).trim();
      return s || null;
    }
    const s = String(v ?? '').trim();
    return s || null;
  };

  // attr_id を直接指定するため、位置(index)対応やフィールド名(type)での紐付けは行わない。
  const freeWords: { attr_id: number; value: string }[] = [];
  for (const aid of order) {
    const sameAttr = candidates.filter((f) => f.attr_id === aid);
    const vals: string[] = [];
    const seenVal = new Set<string>();

    for (const f of sameAttr) {
      if (Array.isArray(f.value)) {
        for (const s of labelsFromMultiLocal(f.value)) {
          if (s && !seenVal.has(s)) {
            seenVal.add(s);
            vals.push(s);
          }
        }
      } else {
        const s = scalarizeOneLocal(f.value);
        if (s && !seenVal.has(s)) {
          seenVal.add(s);
          vals.push(s);
        }
      }
    }

    if (vals.length > 0) {
      for (const v of vals) freeWords.push({ attr_id: aid, value: v });
    } else {
      // キー(attr_id)自体は送ることで、編集時の明示クリアを可能にする
      freeWords.push({ attr_id: aid, value: '' });
    }
  }

  (payload as any).freeWords = freeWords;
};

/* =========================================================
   共通フリーの抽出（旧/新名称対応）→ payload.free_1
 * ========================================================= */
const extractCommonFreeValue = (fields: DynamicFieldItem[]): string | null => {
  const hit = findCommonFreeField(fields);
  if (!hit) return null;

  const v = hit.value;
  if (Array.isArray(v)) {
    const arr = labelsFromMulti(v);
    return arr.length ? arr.join(',') : null;
  }
  return scalarizeOne(v);
};

/** selectionPayload.fixed を DynamicFieldItem[] にもマッピングする（承認系は注入しない） */
const nonEmpty = (x: any): string | null => {
  if (x == null) return null;
  if (typeof x === 'object') {
    const s = String(x.value ?? x.label ?? x.name ?? '').trim();
    return s || null;
  }
  const s = String(x).trim();
  return s || null;
};

const fixedToFields = (p?: SearchSelectionPayload | null): DynamicFieldItem[] => {
  if (!p?.fixed) return [];
  const out: DynamicFieldItem[] = [];

  if ((p.fixed as any).team && nonEmpty((p.fixed as any).team)) {
    out.push({
      id: 'fixed-team',
      type: 'チーム',
      label: 'チーム',
      value: {
        value: nonEmpty((p.fixed as any).team)!,
        label: (p.fixed as any).team?.label ?? nonEmpty((p.fixed as any).team)!,
      },
      isRemovable: false,
    } as any);
  }

  if ((p.fixed as any).dateRange) {
    out.push({
      id: 'fixed-dateRange',
      type: '開始終了日付',
      label: '開始終了日付',
      value: {
        from: (p.fixed as any).dateRange.from,
        to: (p.fixed as any).dateRange.to,
      },
      isRemovable: false,
    } as any);
  }

  return out;
};

/** searchSelectionPayload.entries -> DynamicFieldItem[] へ変換 */
const coerceFieldsFromSearchPayload = (
  p?: SearchSelectionPayload | null
): DynamicFieldItem[] => {
  const list: DynamicFieldItem[] = [];
  if ((p as any)?.entries?.length) {
    list.push(
      ...(((p as any).entries as any[]).map((e: any, i: number) => ({
        id: `sp-${i}-${e.type}`,
        type: e.type,
        label: e.label || e.type,
        value: e.raw,
        isRemovable: false,
        ...(typeof e.attr_id === 'number' ? { attr_id: e.attr_id } : {}),
      })) as DynamicFieldItem[])
    );
  }
  list.push(...fixedToFields(p));
  return list;
};

/* =========================================================
   チーム関連
 * ========================================================= */

const getTeamIdFromSelectionRobust = (p?: SearchSelectionPayload | null): number | null => {
  if (!p) return null;

  const fromFixed = toInt(
    (p.fixed as any)?.team?.value ??
      (p.fixed as any)?.team?.id ??
      (p.fixed as any)?.team?.label ??
      null
  );
  if (fromFixed !== null) return fromFixed;

  const isTeamType = (s?: string) => {
    if (!s) return false;
    const t = s.trim().toLowerCase().replace(/\s+/g, '');
    return t === 'team' || s === 'チーム';
  };

  for (const e of (p as any).entries ?? []) {
    if (!isTeamType(e.type) && !isTeamType(e.label)) continue;

    const raw = e.raw;
    const tryOne = (x: any): number | null => {
      if (x == null) return null;
      if (typeof x === 'object') {
        const cand =
          x.value ??
          x.id ??
          x.label ??
          x.name ??
          null;
        return toInt(cand);
      }
      return toInt(x);
    };

    if (Array.isArray(raw)) {
      for (const x of raw) {
        const n = tryOne(x);
        if (n !== null) return n;
      }
    } else {
      const n = tryOne(raw);
      if (n !== null) return n;
    }
  }

  return null;
};

// いま画面上に“チームが選ばれている”と判断できるか
const hasTeamSelectionNow = (p?: SearchSelectionPayload | null): boolean => {
  const t: any = (p as any)?.fixed?.team;
  if (t == null) return false;
  if (typeof t === 'object') {
    const idOrValue = String((t.value ?? t.id ?? '')).trim();
    const label = String((t.label ?? t.name ?? '')).trim();
    return !!idOrValue || !!label;
  }
  return String(t).trim().length > 0;
};

const wasTeamExplicitlyCleared = (p?: SearchSelectionPayload | null): boolean => {
  if (!p) return false;
  if (hasTeamSelectionNow(p)) return false;
  return (p as any)?.meta?.teamCleared === true;
};

// =========================================================
// Overwrite: respondents_1 を SearchSelectionPayload から作る
// =========================================================

type SearchSelectionPayloadLike = {
  fixed?: {
    taskMembers?: string[];
  };
  displayMap?: Record<string, string>;
};

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();

// "A, B、C" みたいな文字列を配列へ（保険）
const splitNames = (raw: string): string[] => {
  const s = norm(raw);
  if (!s) return [];
  return s
    .split(/\s*[,、，]\s*/)
    .map((x) => norm(x))
    .filter(Boolean);
};

// payload から respondents_1 を安定生成
export const buildRespondents1 = (
  payload: SearchSelectionPayloadLike | null | undefined
): string[] => {
  // 1) 正攻法：fixed.taskMembers（すでに配列で持ってる）
  const fromFixed = Array.isArray(payload?.fixed?.taskMembers)
    ? payload!.fixed!.taskMembers!.map(norm).filter(Boolean)
    : [];

  if (fromFixed.length > 0) return Array.from(new Set(fromFixed));

  // 2) 保険：displayMap["連名者"] から分解（旧ルート）
  const dm = payload?.displayMap ?? {};
  const fromDisplay = splitNames(dm['連名者'] ?? '');

  return Array.from(new Set(fromDisplay));
};


/* =========================================================
   クリア判定
 * ========================================================= */
type ClearedFlags = {
  reportType: boolean;
  project: boolean;
  powertrain: boolean;
  shikimuke: boolean;
  lot: boolean;
};

const hasAnyValueForType = (fields: DynamicFieldItem[], typeName: string): boolean => {
  const hits = fieldsOfType(fields, typeName);
  for (const f of hits) {
    const v = (f as any).value;
    if (Array.isArray(v)) {
      if (labelsFromMulti(v).length > 0) return true;
    } else {
      if (scalarizeOne(v)) return true;
    }
  }
  return false;
};

const hasAnySelectWithNonEmptyIdForType = (fields: DynamicFieldItem[], typeName: string): boolean => {
  const hits = fieldsOfType(fields, typeName);
  const hasNonEmptyId = (x: any): boolean => {
    if (x == null) return false;
    if (typeof x === 'object') {
      const id = String(x.id ?? x.value ?? '').trim();
      return id.length > 0;
    }
    return String(x).trim().length > 0;
  };
  for (const f of hits) {
    const v = (f as any).value;
    if (Array.isArray(v)) {
      if (v.some(hasNonEmptyId)) return true;
    } else if (hasNonEmptyId(v)) {
      return true;
    }
  }
  return false;
};

const isEmptyStringish = (v: any): boolean => {
  const s = (v ?? '').toString().trim();
  return s.length === 0;
};

const computeClearedFlags = (
  selPayload: SearchSelectionPayload | null | undefined,
  fields: DynamicFieldItem[]
): ClearedFlags => {
  const ex = (selPayload as any)?.extras ?? {};

  const reportTypeCleared =
    Object.prototype.hasOwnProperty.call(ex, 'reportType')
      ? isEmptyStringish(ex.reportType)
      : !hasAnyValueForType(fields, CLASSIFICATION_MARKET_TARGET_ATTR_NAME);

  const shikimukeCleared =
    Object.prototype.hasOwnProperty.call(ex, 'shikimuke')
      ? isEmptyStringish(ex.shikimuke)
      : !hasAnyValueForType(fields, FIXED_ATTR_NAME_SHIMUKE);

  const lotCleared =
    Object.prototype.hasOwnProperty.call(ex, 'lot')
      ? isEmptyStringish(ex.lot)
      : !hasAnyValueForType(fields, FIXED_ATTR_NAME_LOT);

  const powertrainCleared =
    Object.prototype.hasOwnProperty.call(ex, 'powertrain')
      ? isEmptyStringish(ex.powertrain)
      : !hasAnySelectWithNonEmptyIdForType(fields, FIXED_ATTR_NAME_POWERTRAIN);

  const projectCleared =
    Object.prototype.hasOwnProperty.call(ex, 'project')
      ? isEmptyStringish(ex.project)
      : !hasAnySelectWithNonEmptyIdForType(fields, FIXED_ATTR_NAME_PROJECT);

  return {
    reportType: reportTypeCleared,
    project: projectCleared,
    powertrain: powertrainCleared,
    shikimuke: shikimukeCleared,
    lot: lotCleared,
  };
};

/* =========================================================
   Component
 * ========================================================= */
const Footer: React.FC<Props> = ({
  wrId,
  activeTab,
  division,
  reporter: authorName,
  startDate,
  endDate,

  draftTitle,
  draftBody,

  searchResults,
  title,
  setTitle,
  body,
  setBody,
  enlightSyncEnabled,
  setEnlightSyncEnabled,
  autoSaveEnabled,
  setAutoSaveEnabled,
  resetDraftTitle,
  resetDraftBody,
  onSuccess,
  isFormValid,
  validateForm,

  approverAM,
  approver3,

  modalPinned,
  createBy: _createBy,
  updateBy: _updateBy,
  getDynamicFields,
  getDynamicAttributes,
  team,
  overwriteTeamId,

  resolvedDeptId,
  resolvedTeamIdForNew,

  remainingChars,

  temporaryFlg,
  loginId,
  images,
  onResetImages,

  onClearNewDates,
  setStartDate: _setStartDate,
  setEndDate: _setEndDate,
  setSearchSelectionPayload,
  enlightSyncToggleDisabled = false,
  enlightSyncToggleDisabledReason = '',

  fbNote,

  getSearchSelectionPayload,
}) => {
  const [isPosting, setIsPosting] = useState(false);
  const [isCheckingPublicScope, setIsCheckingPublicScope] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // ブラウザ標準の alert() の代わりに表示するエラーダイアログ
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const showAlert = (message: string) => setAlertMessage(message);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publicationScopeDetails, setPublicationScopeDetails] = useState<
    PublicationScopeDetail[]
  >([]);
  const { divisionOptionsRaw } = useDivisionOptions();
  const { teamOptionsRaw } = useTeamOptions();
  const navigate = useNavigate();
  const userName = useUserName();
  const UPDATE_BY = userName;

  const hideDueToNoDivision = !division;
  const isOverwrite = activeTab === 'overwrite';
  const n = (v?: string | null) => (v && v.trim().length > 0 ? v : null);
  const toWrId = (raw: any): number | null => {
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : null;
  };
  const resolveWrIdFromResponse = (resp: any): number | null =>
    toWrId(
      resp?.wr_id ??
        resp?.wrId ??
        resp?.id ??
        resp?.data?.wr_id ??
        resp?.data?.wrId ??
        resp?.data?.id
    );

  function buildMergedFields(): DynamicFieldItem[] {
    const baseFields = getDynamicFields?.() ?? [];
    const p = getSearchSelectionPayload?.() ?? null;

    const fromSelection = coerceFieldsFromSearchPayload(p);

    const seenTypes = new Set((baseFields ?? []).map((f) => f?.type));
    const merged: DynamicFieldItem[] = [
      ...baseFields,
      ...fromSelection.filter((f) => !seenTypes.has(f.type)),
    ];

    const sanitized: DynamicFieldItem[] = merged;

    if (fieldsOfType(sanitized, 'チーム').length === 0) {
      const tVal =
        (p as any)?.fixed?.team?.value ??
        (p as any)?.fixed?.team?.label ??
        '';
      if (String(tVal).trim() !== '') {
        sanitized.push({
          id: '__fixed-team',
          type: 'チーム',
          label: 'チーム',
          value: {
            value: tVal,
            label: (p as any)?.fixed?.team?.label ?? String(tVal),
          },
        } as any);
      }
    }
    return sanitized;
  }

  function getOverwriteTeamIdStrict(): number | null {
    const p = getSearchSelectionPayload?.() ?? null;

    if (wasTeamExplicitlyCleared(p)) return null;

    const teamPropId = toInt(team);
    if (teamPropId !== null && teamPropId > 0) return teamPropId;

    const overwriteStateId = toInt(overwriteTeamId);
    if (overwriteStateId !== null && overwriteStateId > 0) return overwriteStateId;

    const fixedId =
      toInt((p as any)?.fixed?.team?.value) ??
      toInt((p as any)?.fixed?.team?.id) ??
      null;
    if (fixedId !== null && fixedId > 0) return fixedId;

    const fromEntries = getTeamIdFromSelectionRobust(p);
    if (fromEntries !== null && fromEntries > 0) return fromEntries;

    const merged = buildMergedFields();
    const idsFromFields = extractTeamIdsFromFields(merged);
    if (idsFromFields.length > 0 && idsFromFields[0] > 0) return idsFromFields[0];

    const labelMaybe =
      (p as any)?.fixed?.team?.label ??
      (p as any)?.fixed?.team ??
      null;
    const parsed = toInt(labelMaybe);
    if (parsed !== null && parsed > 0) return parsed;

    return null;
  }

  const attachAllDynamicPieces = (
    payload: Record<string, any>,
    fields: DynamicFieldItem[],
    _opts: { baseTeamId: number | null }
  ) => {
    const hasCommonFree = findCommonFreeField(fields) !== null;
    if (hasCommonFree) {
      const commonFree = extractCommonFreeValue(fields);
      (payload as any).free_1 = commonFree ?? '';
    }

    attachApproversEnumerated(payload, fields);
    attachRespondentsEnumerated(payload, fields);
    attachFixedEnumerated(payload, fields);
    attachIndexedFreeWordsPairedByType(payload, fields);

    delete (payload as any).approver;
    delete (payload as any).approverAm;
    delete (payload as any).approverAM_1;
    delete (payload as any).respondents;
    delete (payload as any).taskmenber;
  };

  const wasSelectorExplicitlyCleared = (
    p: SearchSelectionPayload | null | undefined,
    key: 'approver3' | 'approverAM'
  ): boolean => {
    const metaKey = key === 'approverAM' ? 'approverAmCleared' : 'approver3Cleared';
    return (p as any)?.meta?.[metaKey] === true;
  };

  const safeJsonParse = <T,>(raw: string): T | null => {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  };

  const clearNewDatesInMemory = useCallback(() => {
    // ★ SearchArea側で datesClearedRef を同期的に立てて persist までやる
    onClearNewDates?.();

    // payload 側の dateRange / entries も落とす（これは Footer 側の責務でOK）
    const cur = getSearchSelectionPayload?.() ?? null;
    if (!cur) {
      setSearchSelectionPayload?.(null);
      return;
    }

    const nextEntries = ((cur as any).entries ?? []).filter((e: any) => {
      const t = String(e?.type ?? '').trim();
      const l = String(e?.label ?? '').trim();
      return t !== '開始終了日付' && l !== '開始終了日付';
    });

    const nextPayload: SearchSelectionPayload = {
      ...(cur as any),
      entries: nextEntries,
      fixed: {
        ...((cur as any).fixed ?? {}),
        dateRange: null,
      },
    };

    setSearchSelectionPayload?.(nextPayload);
  }, [getSearchSelectionPayload, onClearNewDates, setSearchSelectionPayload]);

  const clearNewDraftDatesInLocalStorage = useCallback(() => {
    const rawLoginId = (loginId ?? '').trim();
    if (rawLoginId.length === 0) {
      console.warn('[Footer] clearNewDraftDatesInLocalStorage skipped: loginId is empty');
      return;
    }

    const normalizedLoginId = rawLoginId.toLowerCase();

    // weekly_last_new_draft_v1/v2:<email>:<deptId> を “email大文字小文字差分” も含めて拾う
    const keysToProcess: string[] = [];
    const re = /^weekly_last_new_draft_v[12]:(.+?):(\d+)$/;

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;

      const m = k.match(re);
      if (!m) continue;

      const emailInKey = String(m[1] ?? '').trim().toLowerCase();
      if (emailInKey !== normalizedLoginId) continue;

      keysToProcess.push(k);
    }

    for (let i = 0; i < keysToProcess.length; i++) {
      const storageKey = keysToProcess[i];
      const raw = localStorage.getItem(storageKey);
      if (!raw) continue;

      const obj = safeJsonParse<Record<string, any>>(raw);
      if (!obj || typeof obj !== 'object') continue;

      // root の startDate/endDate をクリア（プロパティが無くても作る）
      obj.startDate = '';
      obj.endDate = '';

      // fields 内の「開始終了日付」もクリア（混入防止）
      if (Array.isArray((obj as any).fields)) {
        for (const f of (obj as any).fields) {
          if (!f || typeof f !== 'object') continue;
          const t = String((f as any).type ?? '').trim();
          if (t !== '開始終了日付') continue;

          (f as any).value = { from: '', to: '' };
        }
      }

      // 将来 fixed.dateRange 形式が混ざっても安全に
      if ((obj as any).fixed?.dateRange) (obj as any).fixed.dateRange = null;

      localStorage.setItem(storageKey, JSON.stringify(obj)); // 既存キーは上書き更新される

    }

    // もし個別キーで持ってる実装があっても消す（無ければ何もしない）
    localStorage.removeItem(`inputEdit:startDate:new:${rawLoginId}`);
    localStorage.removeItem(`inputEdit:endDate:new:${rawLoginId}`);

  }, [loginId]);

const performRegister = async (
    enlight: boolean,
    autoSave: boolean,
    publicScopeConfirmed = false
) => {
    const currentSel = getSearchSelectionPayload?.() ?? null;

    // =========================================================
    // Overwrite/New 共通：連名者（respondents_1）を “配列” で作る
    // =========================================================
    const normName = (s: any) => String(s ?? '').normalize('NFKC').trim();

    const uniqNames = (arr: string[]) => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const x of arr) {
            const v = normName(x);
            if (!v) continue;
            if (seen.has(v)) continue;
            seen.add(v);
            out.push(v);
        }
        return out;
    };

    const splitNames = (raw: any): string[] => {
        const s = normName(raw);
        if (!s) return [];
        return s
            .split(/\s*[,、，]\s*/)
            .map((x) => normName(x))
            .filter(Boolean);
    };

    const buildRespondents1 = (sel: any): { value: string[]; hasExplicitKey: boolean } => {
        const fixed = sel?.fixed;
        const hasExplicitKey =
            !!fixed && Object.prototype.hasOwnProperty.call(fixed, 'taskMembers');

        const fromFixed = Array.isArray(fixed?.taskMembers)
            ? uniqNames((fixed.taskMembers as any[]).map((x) => normName(x)))
            : [];

        if (fromFixed.length > 0) return { value: fromFixed, hasExplicitKey };

        // フォールバック（旧ルート / 保険）
        const dm = sel?.displayMap ?? {};
        const fromDisplay = uniqNames(splitNames(dm?.['連名者'] ?? ''));

        return { value: fromDisplay, hasExplicitKey };
    };

    if (activeTab === 'new') {
        const missingRequired = !validateForm();
        // 文字数超過ではブロックしない
        if (missingRequired) {
            return;
        }
    } else if (isOverwrite) {
        // Overwrite 側も文字数超過ではブロックしない
    }

    if (isPosting || isCheckingPublicScope) return;

    if (!autoSave && !publicScopeConfirmed) {
        setIsCheckingPublicScope(true);
        let shouldWarn = false;
        let scopeDetails: PublicationScopeDetail[] = [];
        try {
            const settings = await fetchLatestWeeklyReportPublicSettings();
            const fields = buildMergedFields();
            const currentSelection = getSearchSelectionPayload?.() ?? null;
            const sourceDepartmentId =
                typeof resolvedDeptId === 'number' ? resolvedDeptId : toInt(division);
            const selectedTeamIdFromProp = toInt(team);
            const selectedTeamId = isOverwrite
                ? getOverwriteTeamIdStrict()
                : (getTeamIdFromSelectionRobust(currentSelection) ??
                  (selectedTeamIdFromProp === 0 ? null : selectedTeamIdFromProp)) ??
                  (typeof resolvedTeamIdForNew === 'number'
                    ? resolvedTeamIdForNew
                    : overwriteTeamId ?? null);
            const sourceDepartment = divisionOptionsRaw.find(
                (department) => Number(department.department_id) === sourceDepartmentId
            );

            shouldWarn = shouldWarnBeforeWeeklyReportPublication({
                fields,
                settings,
                sourceDepartmentId,
                sourceTeamId: selectedTeamId,
                sourceDepartmentCode: sourceDepartment?.label ?? sourceDepartment?.value ?? division,
            });
            if (shouldWarn) {
                const displayByOptionId = buildWeeklyReportPublicScopeDisplayMap({
                    rows: settings,
                    departments: divisionOptionsRaw.map((department) => ({
                        department_id: department.department_id,
                        label: department.label,
                        value: department.value,
                    })),
                    teams: teamOptionsRaw.map((teamOption) => ({
                        team_id: teamOption.team_id,
                        label: teamOption.label,
                        value: teamOption.value,
                    })),
                    maxVisible: Number.MAX_SAFE_INTEGER,
                });
                scopeDetails = getSelectedWeeklyReportOptions(fields).map(
                    ({ optionId, label }) => ({
                        optionId,
                        classificationLabel: label,
                        scopeText:
                            (displayByOptionId[optionId] ?? DEFAULT_PUBLIC_SCOPE_DISPLAY)
                                .summaryText,
                    })
                );
            }
        } catch (error) {
            console.error('公開範囲の確認に失敗しました:', error);
            showAlert('公開範囲を確認できませんでした。時間をおいて再度発行してください。');
            return;
        } finally {
            setIsCheckingPublicScope(false);
        }

        if (shouldWarn) {
            setPublicationScopeDetails(scopeDetails);
            setPublishConfirmOpen(true);
            return;
        }
    }

    setIsPosting(true);

    try {
        const controller = new AbortController();
        let registerResult: any = null;

        if (isOverwrite) {
            if (!wrId) {
                showAlert('上書き対象の週報を選択してください');
                setIsPosting(false);
                return;
            }
            const titleOk = n(title);
            const bodyOk = n(body);
            if (!titleOk || !bodyOk) {
                showAlert('上書きでは「タイトル」と「本文」だけが必須です。入力してください。');
                setIsPosting(false);
                return;
            }

            const fields = buildMergedFields();
            const missingDynamic = missingRequiredDynamicTypes(
                fields,
                getDynamicAttributes?.()
            );
            if (missingDynamic.length > 0) {
                showAlert(`必須の動的属性が入力されていません: ${missingDynamic.join(', ')}`);
                setIsPosting(false);
                return;
            }

            const overwritePayload: any = {
                wr_id: wrId,
                title: titleOk,
                body: bodyOk,
                feedback_body: fbNote ?? '',
                sync_flg: enlight ? 1 : 0,
                temporary_flg: autoSave ? 1 : 0,
            };

            // loginId、update_by_user_id に入れる
            if (loginId && loginId.trim().length > 0) {
                overwritePayload.update_by_user_id = loginId.trim();
            }

            const update_by = UPDATE_BY;
            if (update_by) overwritePayload.update_by = update_by;

            const overwriteReporter =
                pickSelectorValue((currentSel as any)?.fixed?.reporter) ??
                firstLabelOfType(fields, '記入者');
            if (overwriteReporter) {
                overwritePayload.create_by = overwriteReporter;
                overwritePayload.createBy = overwriteReporter;
            }

            const selFrom = normalizeDate((currentSel as any)?.fixed?.dateRange?.from ?? null);
            const selTo = normalizeDate((currentSel as any)?.fixed?.dateRange?.to ?? null);
            const sd = selFrom ?? normalizeDate(startDate);
            const ed = selTo ?? normalizeDate(endDate);
            if (sd) overwritePayload.startDate = sd;
            if (ed) overwritePayload.endDate = ed;

            if (typeof resolvedDeptId === 'number') {
                overwritePayload.division = resolvedDeptId;
            }

            // チーム（現在の選択のみ尊重）
            const selectedTeamId = getOverwriteTeamIdStrict();
            const noTeamSelectedNow = selectedTeamId === null;

            let fieldsForAttach: DynamicFieldItem[] = fields;
            let baseTeamIdForEnum: number | null = null;

            if (noTeamSelectedNow) {
                overwritePayload.team = null;
                fieldsForAttach = fields.filter((f: DynamicFieldItem) => f.type !== 'チーム');
                baseTeamIdForEnum = null;
                for (const k of Object.keys(overwritePayload)) {
                    if (/^teamId_\d+$/.test(k)) delete overwritePayload[k];
                }
            } else {
                overwritePayload.team = selectedTeamId!;
                baseTeamIdForEnum = selectedTeamId!;
            }

            const cleared = computeClearedFlags(currentSel ?? null, fields);

            const K = FIXED_ENUM_KEYS;
            if (cleared.reportType) overwritePayload[`${K[CLASSIFICATION_MARKET_TARGET_ATTR_NAME]}_1`] = null;
            if (cleared.project) overwritePayload[`${K[FIXED_ATTR_NAME_PROJECT]}_1`] = null;
            if (cleared.powertrain) overwritePayload[`${K[FIXED_ATTR_NAME_POWERTRAIN]}_1`] = null;
            if (cleared.shikimuke) overwritePayload[`${K[FIXED_ATTR_NAME_SHIMUKE]}_1`] = null;
            if (cleared.lot) overwritePayload[`${K[FIXED_ATTR_NAME_LOT]}_1`] = null;

            if (cleared.reportType) overwritePayload.reportType_1 = null;
            if (cleared.powertrain) overwritePayload.powerTrain_1 = null;
            if (cleared.shikimuke) overwritePayload.Shimuke_1 = null;

            if (!hasAnySelectWithNonEmptyIdForType(fieldsForAttach, FIXED_ATTR_NAME_POWERTRAIN)) {
                if (!('powertrain_1' in overwritePayload)) overwritePayload.powertrain_1 = null;
                if (!('powerTrain_1' in overwritePayload)) overwritePayload.powerTrain_1 = null;
            }

            attachAllDynamicPieces(overwritePayload, fieldsForAttach, {
                baseTeamId: baseTeamIdForEnum,
            });

            // =========================================================
            // 連名者は respondents_1（配列）で送る
            // - fixed.taskMembers が “キーとして存在する” 場合は、空配列でも明示送信（＝クリア扱い）
            // =========================================================
            {
                const { value, hasExplicitKey } = buildRespondents1(currentSel);
                if (hasExplicitKey) {
                    overwritePayload.respondents_1 = value; // 空でも送る（明示クリア）
                } else if (value.length > 0) {
                    overwritePayload.respondents_1 = value;
                }
            }

            // 承認の null 明示送信ロジック（固定欄の指定を優先）
            const finalA3 = pickSelectorValue((currentSel as any)?.fixed?.approver3);
            const finalAM = pickSelectorValue((currentSel as any)?.fixed?.approverAM);
            const a3Cleared = wasSelectorExplicitlyCleared(currentSel, 'approver3');
            const amCleared = wasSelectorExplicitlyCleared(currentSel, 'approverAM');

            // fixed にキー自体が存在しており値が空 → それは「未選択」の明示とみなして null を送る
            const hasA3Fixed = Boolean(
                (currentSel as any)?.fixed &&
                    Object.prototype.hasOwnProperty.call((currentSel as any).fixed, 'approver3')
            );
            const hasAMFixed = Boolean(
                (currentSel as any)?.fixed &&
                    Object.prototype.hasOwnProperty.call((currentSel as any).fixed, 'approverAM')
            );

            if (a3Cleared || (hasA3Fixed && finalA3 === null)) {
                overwritePayload.approver_1 = null;
            } else if (finalA3 !== null) {
                overwritePayload.approver_1 = finalA3;
            }

            if (amCleared || (hasAMFixed && finalAM === null)) {
                overwritePayload.approverAm_1 = null;
            } else if (finalAM !== null) {
                overwritePayload.approverAm_1 = finalAM;
            }

            delete overwritePayload.approverAM_1;

            if (noTeamSelectedNow) {
                for (const k of Object.keys(overwritePayload)) {
                    if (/^teamId_\d+$/.test(k) && k !== 'teamId_1') delete overwritePayload[k];
                }
            }

            /* === approver_1 / approverAm_1 のフォールバック補完（明示クリア時は上書きしない） === */
            if (!Object.prototype.hasOwnProperty.call(overwritePayload, 'approver_1')) {
                const a3FromFields = firstLabelOfType(fieldsForAttach, '承認-3');
                if (a3FromFields) {
                    overwritePayload.approver_1 = a3FromFields;
                } else {
                    const a3FromProps = n(approver3);
                    if (a3FromProps) overwritePayload.approver_1 = a3FromProps;
                }
            }

            if (!Object.prototype.hasOwnProperty.call(overwritePayload, 'approverAm_1')) {
                const amFromFields = firstLabelOfType(fieldsForAttach, '承認AM');
                if (amFromFields) {
                    overwritePayload.approverAm_1 = amFromFields;
                } else {
                    const amFromProps = n(approverAM);
                    if (amFromProps) overwritePayload.approverAm_1 = amFromProps;
                }
            }

            registerResult = await postWeeklyReportOverwrite(overwritePayload, controller.signal);
        } else {
            // Create New
            const fields = buildMergedFields();

            const payload: Record<string, any> = {
                title: draftTitle,
                body: draftBody,
                createBy: n(_createBy) ?? authorName,
                updateBy: n(_updateBy),
                temporary_flg: autoSave ? 1 : 0,
                autoSaveEnabled: autoSave,
                feedback_body: fbNote ?? '',
            };

            // メールアドレス
            if (loginId && loginId.trim().length > 0) {
                payload.create_by_user_id = loginId.trim();
                payload.update_by_user_id = loginId.trim();
            }

            const selFrom = normalizeDate((currentSel as any)?.fixed?.dateRange?.from ?? null);
            const selTo = normalizeDate((currentSel as any)?.fixed?.dateRange?.to ?? null);
            const sd = selFrom ?? normalizeDate(startDate);
            const ed = selTo ?? normalizeDate(endDate);
            if (sd) (payload as any).startDate = sd;
            if (ed) (payload as any).endDate = ed;

            const deptIdForPayload =
                typeof resolvedDeptId === 'number' ? resolvedDeptId : toInt(division);
            if (deptIdForPayload !== null) {
                (payload as any).division = deptIdForPayload;
            }

            const selectedTeamId = getTeamIdFromSelectionRobust(currentSel);
            const selectedTeamIdFromProp = toInt(team);
            const normalizedTeamId =
                selectedTeamId ?? (selectedTeamIdFromProp === 0 ? null : selectedTeamIdFromProp);

            const baseTeamId =
                normalizedTeamId ??
                (typeof resolvedTeamIdForNew === 'number'
                    ? resolvedTeamIdForNew
                    : overwriteTeamId ?? null);

            if (baseTeamId != null) {
                (payload as any).team = baseTeamId;
            }

            attachAllDynamicPieces(payload, fields, { baseTeamId: baseTeamId ?? null });

            // =========================================================
            // 連名者は respondents_1（配列）で送る（NEWも同様）
            // - fixed.taskMembers が “キーとして存在する” 場合は、空配列でも明示送信（＝クリア扱い）
            // =========================================================
            {
                const { value, hasExplicitKey } = buildRespondents1(currentSel);
                if (hasExplicitKey) {
                    (payload as any).respondents_1 = value; // 空でも送る（明示クリア）
                } else if (value.length > 0) {
                    (payload as any).respondents_1 = value;
                }
            }

            // New ：固定欄に「キーが存在」しているときだけ上書きする（存在しないなら dynamic からの値を維持）
            const hasA3Fixed = Boolean(
                (currentSel as any)?.fixed &&
                    Object.prototype.hasOwnProperty.call((currentSel as any).fixed, 'approver3')
            );
            const hasAMFixed = Boolean(
                (currentSel as any)?.fixed &&
                    Object.prototype.hasOwnProperty.call((currentSel as any).fixed, 'approverAM')
            );

            const a3Cleared = wasSelectorExplicitlyCleared(currentSel, 'approver3');
            const amCleared = wasSelectorExplicitlyCleared(currentSel, 'approverAM');

            const finalA3 = pickSelectorValue((currentSel as any)?.fixed?.approver3);
            const finalAM = pickSelectorValue((currentSel as any)?.fixed?.approverAM);

            if (a3Cleared || (hasA3Fixed && finalA3 === null)) {
                (payload as any).approver_1 = null; // 明示クリア
            } else if (hasA3Fixed && finalA3 !== null) {
                (payload as any).approver_1 = finalA3; // 固定欄で指定あり → 上書き
            }

            if (amCleared || (hasAMFixed && finalAM === null)) {
                (payload as any).approverAm_1 = null;
            } else if (hasAMFixed && finalAM !== null) {
                (payload as any).approverAm_1 = finalAM;
            }
            delete (payload as any).approverAM_1; // 表記ゆれは常に消す

            (payload as any).syncFlg = enlight ? 1 : 0;
            (payload as any).sync_flg = enlight ? 1 : 0;

            /* === (2) 追加: approver_1 / approverAm_1 のフォールバック補完（明示クリア時は上書きしない） === */
            if (!Object.prototype.hasOwnProperty.call(payload, 'approver_1')) {
                const a3FromFields = firstLabelOfType(fields, '承認-3');
                if (a3FromFields) {
                    (payload as any).approver_1 = a3FromFields;
                } else {
                    const a3FromProps = n(approver3);
                    if (a3FromProps) (payload as any).approver_1 = a3FromProps;
                }
            }

            if (!Object.prototype.hasOwnProperty.call(payload, 'approverAm_1')) {
                const amFromFields = firstLabelOfType(fields, '承認AM');
                if (amFromFields) {
                    (payload as any).approverAm_1 = amFromFields;
                } else {
                    const amFromProps = n(approverAM);
                    if (amFromProps) (payload as any).approverAm_1 = amFromProps;
                }
            }

            registerResult = await postWeeklyReport(payload, controller.signal);

            // 週報本体の公開状態は変えず、保存
            const newWrId = resolveWrIdFromResponse(registerResult);
            if (newWrId !== null) {
                try {
                    await postNewWeeklyReportVersion(
                        { wr_id: newWrId },
                        controller.signal
                    );
                } catch (versionErr) {
                    console.warn('初回バージョンの自動保存に失敗しました:', versionErr);
                }
            }
        }

        if (images.length > 0) {
            const resolvedWrId = resolveWrIdFromResponse(registerResult) ?? toWrId(wrId);
            if (!resolvedWrId) {
                throw new Error('wr_id is missing for image upload');
            }

            const createByValue = loginId?.trim() || n(_createBy) || undefined;
            const updateByValue = loginId?.trim() || n(_updateBy) || undefined;
            try {
                await uploadImageAttachments({
                    wrId: resolvedWrId,
                    images,
                    createBy: createByValue,
                    updateBy: updateByValue,
                    onResetImages,
                    signal: controller.signal,
                });
            } catch (uploadErr) {
                if (!isOverwrite) {
                    try {
                        await postWeeklyReportDelete({ wr_id: resolvedWrId }, controller.signal);
                    } catch (rollbackErr) {
                        console.error('画像アップロード失敗後のロールバックに失敗:', rollbackErr);
                    }
                }
                throw uploadErr;
            }
        }

        if (isOverwrite) {
            const preferenceWrId = toWrId(wrId);
            if (preferenceWrId != null) {
                persistOverwriteEnlightSyncOutcome(
                    loginId,
                    preferenceWrId,
                    enlight,
                    autoSave
                );
            }
        }

        if (!isOverwrite) {
            clearNewDatesInMemory(); // SearchArea経由で同期クリア
            clearNewDraftDatesInLocalStorage(); // 保険で localStorage も掃除
        }

        notifyWeeklyReportUpdated();

        setTitle('');
        setBody('');
        resetDraftTitle();
        resetDraftBody();
        onSuccess();
        navigate('/', { replace: true, state: { activeTab: 'new' } });
    } catch (err) {
        console.error('登録失敗:', err);
        if (isApiError(err)) {
            if (err.response?.status === 404) {
                showAlert('指定された週報が見つかりません');
            } else if (err.response?.status === 400) {
                if (isOverwrite) {
                    showAlert('入力内容に不備があります（400）。送信値をご確認ください。');
                } else {
                    showAlert('必須項目が不足しています');
                }
            } else if (err.response?.status === 422) {
                showAlert(
                    extractApiErrorMessage(err) ??
                    '入力内容に誤りがあります。各項目をご確認ください。'
                );
            } else {
                showAlert('登録に失敗しました');
            }
        } else {
            showAlert('通信に失敗しました');
        }
    } finally {
        setTimeout(() => setIsPosting(false), 1200);
    }
};


  const handleRegisterClick = async () => {
    return performRegister(enlightSyncEnabled, false);
  };

  const handleDraftSaveClick = async () => {
    if (isPosting || isCheckingPublicScope) return;
    setAutoSaveEnabled(true);
    setEnlightSyncEnabled(false);
    return performRegister(false, true);
  };

  const handleConfirmPublication = async () => {
    if (isPosting || isCheckingPublicScope) return;
    setPublishConfirmOpen(false);
    setPublicationScopeDetails([]);
    return performRegister(enlightSyncEnabled, false, true);
  };

  const handleDeleteClick = async () => {
    if (!isOverwrite) return;
    if (!wrId) return;
    if (isPosting) return;
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!isOverwrite) return;
    if (!wrId) return;
    if (isPosting) return;

    setConfirmOpen(false);
    setIsPosting(true);
    try {
      const controller = new AbortController();
      await postWeeklyReportDelete({ wr_id: wrId }, controller.signal);
      deleteEnlightSyncOverride(wrId);
      deleteRemandEnlightSyncPreference(loginId, wrId);

      setTitle('');
      setBody('');
      resetDraftTitle();
      resetDraftBody();
      navigate('/', {
        replace: true,
        state: { activeTab: 'new', flash: { type: 'success', message: '削除が完了しました' } },
      });
    } catch (err) {
      console.error('削除失敗:', err);
      if (isApiError(err)) {
        if (err.response?.status === 404) {
          showAlert('指定された週報が見つかりません');
        } else if (err.response?.status === 400) {
          showAlert('削除リクエストが不正です（400）');
        } else {
          showAlert('削除に失敗しました');
        }
      } else {
        showAlert('通信に失敗しました');
      }
    } finally {
      setTimeout(() => setIsPosting(false), 1200);
    }
  };

  // NEWタブの必須未入力
  const missingRequiredNew = activeTab === 'new' && !isFormValid;

  // Overwrite 用の必須チェック（タイトル・本文の両方必須）
  const missingRequiredOverwrite = isOverwrite && (!n(title) || !n(body));

  const tooltipLines: string[] = [];
  if (missingRequiredNew) tooltipLines.push('必須入力項目が入力されていません。');
  if (missingRequiredOverwrite) tooltipLines.push('「タイトル」と「本文」は必須です。');

  const hasTooltip = tooltipLines.length > 0;
  const tooltipTitle = (
    <span style={{ whiteSpace: 'pre-line' }}>
      {tooltipLines.join('\n')}
    </span>
  );

  if (hideDueToNoDivision) return null;

  const canShowOverwrite =
    !isOverwrite || modalPinned || (searchResults?.length ?? 0) > 0;

  if (!canShowOverwrite) return null;

  // disabled 判定（260文字超過は無視）
  const isButtonDisabled =
    isPosting ||
    isCheckingPublicScope ||
    (isOverwrite && (!wrId || missingRequiredOverwrite)) ||
    (activeTab === 'new' && !isFormValid);

  const isDeleteDisabled =
    isPosting || isCheckingPublicScope || !wrId;

  // 下書き保存も Overwrite ではタイトル/本文が空なら無効化
  const isDraftDisabled =
    isPosting ||
    isCheckingPublicScope ||
    (isOverwrite && (!wrId || missingRequiredOverwrite));

  // グレー表示（見た目）
  const shouldGreyOut =
    (activeTab === 'new' && !isFormValid) ||
    (isOverwrite && missingRequiredOverwrite);

  return (
    <Box
      className="footer"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 0, p: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {isOverwrite && typeof temporaryFlg === 'number' && (
          temporaryFlg === 1 ? (
            <Chip
              size="small"
              label="下書きで登録されています"
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontWeight: 600 }}
            />
          ) : (
            <Chip
              size="small"
              label="週報連で登録されています"
              color="success"
              variant="outlined"
              sx={{ height: 22, fontWeight: 600 }}
            />
          )
        )}
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mx: 1 }}>
            Enlight同期
          </Typography>
          <Tooltip
            title={enlightSyncToggleDisabled ? enlightSyncToggleDisabledReason : ''}
            disableHoverListener={!enlightSyncToggleDisabled}
            disableFocusListener={!enlightSyncToggleDisabled}
            disableTouchListener={!enlightSyncToggleDisabled}
          >
            <span>
              <CustomSwitch
                checked={enlightSyncEnabled}
                onChange={() => {
                  if (enlightSyncToggleDisabled) return;
                  setEnlightSyncEnabled((p) => !p);
                }}
                color="success"
                disabled={enlightSyncToggleDisabled}
              />
            </span>
          </Tooltip>
        </Box>

        {isOverwrite && (
          <Button
            aria-label="週報削除"
            variant="outlined"
            onClick={handleDeleteClick}
            disabled={isDeleteDisabled}
            color="error"
            sx={{
              borderColor: isDeleteDisabled ? grey[400] : '#e53935',
              color: isDeleteDisabled ? grey[500] : '#e53935',
              '&:hover': {
                borderColor: '#c62828',
                backgroundColor: 'rgba(229, 57, 53, 0.04)',
              },
            }}
          >
            週報削除
          </Button>
        )}

        <AlertDialog
          open={alertMessage !== null}
          message={alertMessage ?? ''}
          onClose={() => setAlertMessage(null)}
        />

        <ConfirmDialog
          open={publishConfirmOpen}
          title="公開範囲の確認"
          description={
            <Box>
              <Box
                sx={(theme) => ({
                  p: { xs: 2, sm: 2.25 },
                  borderRadius: 2,
                  backgroundColor: theme.palette.action.hover,
                })}
              >
                <Typography sx={{ mb: 1.25, fontWeight: 700 }}>
                  現在の公開設定
                </Typography>
                <Stack spacing={1}>
                  {publicationScopeDetails.map((detail) => (
                    <Box
                      key={detail.optionId}
                      sx={(theme) => ({
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'minmax(9rem, 0.8fr) 1.2fr' },
                        gap: { xs: 0.25, sm: 2 },
                        px: { xs: 1.25, sm: 1.5 },
                        py: 1.1,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.background.paper,
                      })}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          週報分類
                        </Typography>
                        <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                          {detail.classificationLabel}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          公開先
                        </Typography>
                        <Typography sx={{ overflowWrap: 'anywhere' }}>
                          {detail.scopeText}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
                <Typography sx={{ mt: 1.5, lineHeight: 1.75 }}>
                  発行者本人は閲覧できますが、同じ課・チームのメンバーは閲覧できません。
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2.25 }}>
                <WarningAmberRoundedIcon color="warning" sx={{ mt: '2px', flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 700, lineHeight: 1.65 }}>
                  この公開範囲で発行しますか？
                </Typography>
              </Box>
            </Box>
          }
          confirmText="週報登録"
          confirmColor="success"
          confirmSx={(theme) => ({
            color: '#fff',
            backgroundColor: theme.palette.success.dark,
            '&:hover': { backgroundColor: theme.palette.success.main },
          })}
          cancelText="キャンセル"
          cancelVariant="outlined"
          showCloseIcon
          responsiveLayout
          maxWidth="sm"
          processing={isPosting}
          onConfirm={handleConfirmPublication}
          onClose={() => {
            setPublishConfirmOpen(false);
            setPublicationScopeDetails([]);
          }}
        />

        <ConfirmDialog
          open={confirmOpen}
          title="削除確認"
          description="選択中の週報を削除します。よろしいですか？この操作は取り消せません。"
          confirmText="削除"
          confirmColor="error"
          confirmVariant="contained"
          processing={isPosting}
          onConfirm={handleConfirmDelete}
          onClose={() => setConfirmOpen(false)}
        />

        <Button
          aria-label="下書き保存"
          variant="contained"
          onClick={handleDraftSaveClick}
          disabled={isDraftDisabled || isButtonDisabled}
          sx={(theme) => ({
            color: '#fff',
            backgroundColor: shouldGreyOut
              ? '#E0E0E0'
              : (isDraftDisabled || isButtonDisabled)
              ? grey[600]
              : theme.palette.info.dark,
            '&:hover': {
              backgroundColor: shouldGreyOut
                ? '#E0E0E0'
                : (isDraftDisabled || isButtonDisabled)
                ? grey[600]
                : theme.palette.info.light,
            },
            '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.7)' },
          })}
        >
          下書き保存
        </Button>

        <Tooltip
          arrow
          placement="top"
          title={hasTooltip ? tooltipTitle : ''}
          disableHoverListener={!hasTooltip}
          disableFocusListener={!hasTooltip}
          disableTouchListener={!hasTooltip}
        >
          <span>
            <Button
              aria-label="週報登録"
              variant="contained"
              onClick={handleRegisterClick}
              disabled={isButtonDisabled}
              sx={(theme) => ({
                color: '#fff',
                backgroundColor: shouldGreyOut
                  ? '#E0E0E0'
                  : isButtonDisabled
                  ? grey[600]
                  : theme.palette.success.dark,
                '&:hover': {
                  backgroundColor: shouldGreyOut
                    ? '#E0E0E0'
                    : isButtonDisabled
                    ? grey[600]
                    : theme.palette.success.main,
                },
                '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.7)' },
              })}
            >
              週報登録
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default Footer;
